import { chatApi } from "@/lib/data/requests/ai-chat";
import type {
  AiChatGuardianReviewStatus,
  AiChatThreadStatus,
  AiChatTimelinePage,
  ChatApi,
} from "@/lib/types/ai-chat";
import {
  type InfiniteData,
  infiniteQueryOptions,
  queryOptions,
  replaceEqualDeep,
} from "@tanstack/react-query";

export const AI_CHAT_HEADER_PAGE_SIZE = 50;
export const AI_CHAT_TIMELINE_PAGE_SIZE = 50;
export const AI_CHAT_TIMELINE_MAX_PAGES = 8;
export const AI_CHAT_SEARCH_PAGE_SIZE = 20;
export const AI_CHAT_REVIEW_PAGE_SIZE = 20;

export const aiChatQueryKeys = {
  all: ["ai-chat"] as const,
  threadLists: () => [...aiChatQueryKeys.all, "threads"] as const,
  threadList: (status?: AiChatThreadStatus) =>
    [...aiChatQueryKeys.threadLists(), status ?? "all"] as const,
  thread: (threadId: string) =>
    [...aiChatQueryKeys.all, "thread", threadId] as const,
  timeline: (threadId: string) =>
    [...aiChatQueryKeys.thread(threadId), "timeline"] as const,
  layout: (threadId: string) =>
    [...aiChatQueryKeys.thread(threadId), "layout"] as const,
  search: (threadId: string, query: string) =>
    [...aiChatQueryKeys.thread(threadId), "search", query] as const,
  reviews: (runId: string, status: AiChatGuardianReviewStatus) =>
    [...aiChatQueryKeys.all, "run", runId, "reviews", status] as const,
  review: (runId: string, reviewId: string) =>
    [...aiChatQueryKeys.all, "run", runId, "review", reviewId] as const,
};

export const aiChatThreadListOptions = ({
  api = chatApi,
  status,
}: {
  api?: ChatApi;
  status?: AiChatThreadStatus;
} = {}) =>
  infiniteQueryOptions({
    queryKey: aiChatQueryKeys.threadList(status),
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam, signal }) =>
      api.listThreads({
        cursor: pageParam,
        limit: AI_CHAT_HEADER_PAGE_SIZE,
        status,
        signal,
      }),
    getNextPageParam: (lastPage) =>
      lastPage.hasMore ? (lastPage.nextCursor ?? undefined) : undefined,
    // Only the selected thread streams; keep other active conversations current.
    refetchInterval: (query) =>
      query.state.data?.pages.some((page) =>
        page.threads.some((thread) =>
          ["running", "pending_approval"].includes(thread.status),
        ),
      )
        ? 5_000
        : false,
    refetchIntervalInBackground: false,
    staleTime: 15_000,
  });

export const aiChatThreadOptions = (
  threadId: string | undefined,
  api: ChatApi = chatApi,
) =>
  queryOptions({
    queryKey: aiChatQueryKeys.thread(threadId ?? "none"),
    enabled: Boolean(threadId),
    queryFn: ({ signal }) => api.getThread(threadId ?? "", signal),
    staleTime: 5_000,
  });

type TimelinePageParam =
  | { direction: "initial" }
  | { direction: "around"; itemId: string }
  | { direction: "before"; cursor: string }
  | { direction: "after"; cursor: string };

export const aiChatTimelineOptions = (
  threadId: string | undefined,
  api: ChatApi = chatApi,
) =>
  infiniteQueryOptions({
    queryKey: aiChatQueryKeys.timeline(threadId ?? "none"),
    enabled: Boolean(threadId),
    initialPageParam: { direction: "initial" } as TimelinePageParam,
    queryFn: ({ pageParam, signal }) =>
      api.getTimeline(threadId ?? "", {
        aroundItemId:
          pageParam.direction === "around" ? pageParam.itemId : undefined,
        before: pageParam.direction === "before" ? pageParam.cursor : undefined,
        after: pageParam.direction === "after" ? pageParam.cursor : undefined,
        limit: AI_CHAT_TIMELINE_PAGE_SIZE,
        signal,
      }),
    getPreviousPageParam: (firstPage) =>
      firstPage.hasMoreBefore && firstPage.beforeCursor
        ? ({
            direction: "before",
            cursor: firstPage.beforeCursor,
          } satisfies TimelinePageParam)
        : undefined,
    getNextPageParam: (lastPage) =>
      lastPage.hasMoreAfter && lastPage.afterCursor
        ? ({
            direction: "after",
            cursor: lastPage.afterCursor,
          } satisfies TimelinePageParam)
        : undefined,
    maxPages: AI_CHAT_TIMELINE_MAX_PAGES,
    structuralSharing: (previous, incoming) => {
      const current = previous as InfiniteData<AiChatTimelinePage> | undefined;
      const next = incoming as InfiniteData<AiChatTimelinePage>;
      const known = new Map(
        current?.pages.flatMap((page) =>
          page.items.map((item) => [item.id, item] as const),
        ),
      );
      return replaceEqualDeep(current, {
        ...next,
        pages: next.pages.map((page) => ({
          ...page,
          items: page.items.map((item) => {
            const cached = known.get(item.id);
            const cachedEnd = Number(
              cached?.payload.endSequence ?? cached?.sequence ?? 0,
            );
            const incomingEnd = Number(
              item.payload.endSequence ?? item.sequence,
            );
            return cached &&
              (cached.revision > item.revision || cachedEnd > incomingEnd)
              ? cached
              : item;
          }),
        })),
      });
    },
    staleTime: 5_000,
  });

export const aiChatSearchOptions = (
  threadId: string | undefined,
  query: string,
  api: ChatApi = chatApi,
) => {
  const normalizedQuery = query.trim();
  return infiniteQueryOptions({
    queryKey: aiChatQueryKeys.search(threadId ?? "none", normalizedQuery),
    enabled: Boolean(threadId) && normalizedQuery.length >= 3,
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam, signal }) =>
      api.searchThread(threadId ?? "", {
        query: normalizedQuery,
        cursor: pageParam,
        limit: AI_CHAT_SEARCH_PAGE_SIZE,
        signal,
      }),
    getNextPageParam: (lastPage) =>
      lastPage.hasMore ? (lastPage.nextCursor ?? undefined) : undefined,
    maxPages: 5,
    staleTime: 30_000,
  });
};

export const aiChatGuardianReviewsOptions = (
  runId: string | undefined,
  status: AiChatGuardianReviewStatus = "awaiting_human",
  api: ChatApi = chatApi,
) =>
  infiniteQueryOptions({
    queryKey: aiChatQueryKeys.reviews(runId ?? "none", status),
    enabled: Boolean(runId),
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam, signal }) =>
      api.listGuardianReviews(runId ?? "", {
        cursor: pageParam,
        limit: AI_CHAT_REVIEW_PAGE_SIZE,
        status,
        signal,
      }),
    getNextPageParam: (lastPage) =>
      lastPage.hasMore ? (lastPage.nextCursor ?? undefined) : undefined,
    maxPages: 5,
    staleTime: 5_000,
  });

export const aiChatGuardianReviewOptions = (
  runId: string | undefined,
  reviewId: string | undefined,
  api: ChatApi = chatApi,
) =>
  queryOptions({
    queryKey: aiChatQueryKeys.review(runId ?? "none", reviewId ?? "none"),
    enabled: Boolean(runId && reviewId),
    queryFn: ({ signal }) =>
      api.getGuardianReview(runId ?? "", reviewId ?? "", signal),
    staleTime: 5_000,
  });
